import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import type { ServiceError } from '@/app/types';
import { useGlobalModal } from './use-global-modal';

export enum ErrorMsg {
  ElementNotFound = 'ElementNotFound',
  ElementExists = 'ElementExists',
  ElementTotalLimitExceeded = 'ElementTotalLimitExceeded',
  ElementMonthLimitExceeded = 'ElementMonthLimitExceeded',
  PermissionDenied = 'PermissionDenied',
  ImageTooLarge = 'ImageTooLarge',
  ImageFormatNotSupport = 'ImageFormatNotSupport',
  ElementOperateLimitExceeded = 'ElementOperateLimitExceeded',
  LabelNotFound = 'LabelNotFound',
  LabelExists = 'LabelExists',
  LabelTotalLimitExceeded = 'LabelTotalLimitExceeded',
  AuditSubmitIllegal = 'AuditSubmitIllegal',
  ElementFreeMonthLimitExceeded = 'ElementFreeMonthLimitExceeded',
  ElementPayMonthLimitExceeded = 'ElementPayMonthLimitExceeded',
  ElementShareNotExists = 'ElementShareNotExists',
  ElementShareCreationNotExists = 'ElementShareCreationNotExists',
  ElementForbidden = 'ElementForbidden',
}

export const useElementErrorMsg = (config?: Partial<Record<ErrorMsg, string>>) => {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const defaultErrorMsgMap: Record<ErrorMsg, string> = {
    [ErrorMsg.ElementNotFound]: t('elements.actionMsg.elementNotFound'),
    [ErrorMsg.ElementExists]: t('elements.actionMsg.elementExists'),
    [ErrorMsg.ElementTotalLimitExceeded]: t('elements.actionMsg.elementTotalLimitExceeded'),
    [ErrorMsg.ElementMonthLimitExceeded]: t('elements.actionMsg.elementMonthLimitExceeded'),
    [ErrorMsg.PermissionDenied]: t('elements.actionMsg.permissionDenied'),
    [ErrorMsg.ImageTooLarge]: t('elements.actionMsg.imageTooLarge'),
    [ErrorMsg.ImageFormatNotSupport]: t('elements.actionMsg.imageFormatNotSupport'),
    [ErrorMsg.ElementOperateLimitExceeded]: t('elements.actionMsg.elementOperateLimitExceeded'),
    [ErrorMsg.LabelNotFound]: t('elements.actionMsg.labelNotFound'),
    [ErrorMsg.LabelExists]: t('elements.actionMsg.labelExists'),
    [ErrorMsg.LabelTotalLimitExceeded]: t('elements.actionMsg.labelTotalLimitExceeded'),
    [ErrorMsg.AuditSubmitIllegal]: t('elements.actionMsg.auditSubmitIllegal'),
    [ErrorMsg.ElementFreeMonthLimitExceeded]: t('elements.actionMsg.elementMonthLimitExceeded'),
    [ErrorMsg.ElementPayMonthLimitExceeded]: t('elements.actionMsg.elementMonthLimitExceeded'),
    [ErrorMsg.ElementShareNotExists]: t('elements.submit.dialog.form.shareFail'),
    [ErrorMsg.ElementShareCreationNotExists]: t('elements.actionMsg.creationNotExists'),
    [ErrorMsg.ElementForbidden]: t('elements.actionMsg.elementForbidden'),
  };

  const errorMsgMap = useMemo(() => {
    return {
      ...defaultErrorMsgMap,
      ...config,
    };
  }, [config]);

  const getErrorMsg = (error: ServiceError, placeholder?: string) => {
    return errorMsgMap[error?.reason as ErrorMsg] || placeholder || error.message || t('popup.toast.fetchError');
  };

  const showErrorMsg = (
    error: ServiceError,
    type?: 'error' | 'info' | 'success' | 'warning' | 'custom',
    placeholder?: string,
  ) => {
    const message = getErrorMsg(error, placeholder);
    if (error.reason === ErrorMsg.AuditSubmitIllegal) {
      const auditId = error.metadata?.audit_id;
      const auditMessage = t('elements.actionMsg.auditSubmitIllegal');
      openConfirmDialog({
        title: t('popup.dialog.error.checkFailed.title'),
        content: auditId ? `${auditMessage}\n(ID: ${auditId})` : auditMessage,
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
    } else {
      toast({
        type: type || 'error',
        message,
      });
    }
  };

  const showSuccessMsg = (params?: {
    message?: string;
    type?: 'error' | 'info' | 'success' | 'warning' | 'custom';
  }) => {
    const { type, message } = params || {};
    toast({
      type: type || 'success',
      message: message || t('elements.actionSuccess'),
    });
  };

  return {
    showErrorMsg,
    getErrorMsg,
    showSuccessMsg,
  };
};
